/**
 * Centralized music track data for the Lumora soundtrack
 * Used by both the main MusicPlayer and MiniMusicPlayer components
 */

export interface MusicTrack {
  id: string;
  title: string;
  file: string;
}

/**
 * Complete soundtrack listing with track IDs for easy reference
 */
export const musicTracks: Record<string, MusicTrack> = {
  'lumora-first-dawn': {
    id: 'lumora-first-dawn',
    title: 'Lumora: The First Dawn',
    file: '/music/Lumora- The First Dawn.mp3',
  },
  'peaceful-voyage-daylight': {
    id: 'peaceful-voyage-daylight',
    title: 'Peaceful Voyage – Daylight at Sea',
    file: '/music/01-01-Peaceful Voyage – Daylight at Sea.mp3',
  },
  'peaceful-voyage-battle': {
    id: 'peaceful-voyage-battle',
    title: 'Peaceful Voyage – Daylight at Sea (Battle)',
    file: '/music/01-02-Peaceful Voyage – Daylight at Sea (battle).mp3',
  },
  'portsmuth-day': {
    id: 'portsmuth-day',
    title: 'Portsmuth in Day',
    file: '/music/Portsmuth in Day.mp3',
  },
  'portsmuth-night': {
    id: 'portsmuth-night',
    title: 'Portsmuth at Night',
    file: '/music/02-00-Portsmuth at Night.mp3',
  },
  'fateful-encounter': {
    id: 'fateful-encounter',
    title: 'A Fateful Encounter',
    file: '/music/02_A Fateful Encounter_trimmed.mp3',
  },
  'moonlight-pursuit': {
    id: 'moonlight-pursuit',
    title: 'Moonlight Pursuit',
    file: '/music/02-02-Moonlight Pursuit.mp3',
  },
  'chains-beneath-crown': {
    id: 'chains-beneath-crown',
    title: 'Chains Beneath the Crown',
    file: '/music/Chains Beneath the Crown.mp3',
  },
  'blades-of-resolve': {
    id: 'blades-of-resolve',
    title: 'Blades of Resolve',
    file: '/music/Blades of Resolve (Main Battle Theme).mp3',
  },
  'echoes-ascetic-day': {
    id: 'echoes-ascetic-day',
    title: 'Echoes of the Ascetic (Day)',
    file: '/music/Echoes of the Ascetic (Democratic Faction Theme - Day).mp3',
  },
  'echoes-ascetic-night': {
    id: 'echoes-ascetic-night',
    title: 'Echoes of the Ascetic – Night Vigil',
    file: '/music/Echoes of the Ascetic – Night Vigil.mp3',
  },
  'throne-iron-day': {
    id: 'throne-iron-day',
    title: 'Throne of Iron (Day)',
    file: '/music/Throne of Iron (Theme for Kratocratic Faction - Day).mp3',
  },
  'throne-iron-night': {
    id: 'throne-iron-night',
    title: 'Throne of Iron (Night)',
    file: '/music/Throne of Iron (Night).mp3',
  },
  'crown-scepter-day': {
    id: 'crown-scepter-day',
    title: 'Crown and Scepter',
    file: '/music/Crown and Scepter (Monarchy Theme).mp3',
  },
  'crown-scepter-night': {
    id: 'crown-scepter-night',
    title: 'Crown and Scepter – Night Repose',
    file: '/music/Crown and Scepter – Night Repose.mp3',
  },
  'overworld-day': {
    id: 'overworld-day',
    title: 'Lumora Overworld Theme',
    file: '/music/Lumora Overworld Theme.mp3',
  },
  'overworld-night': {
    id: 'overworld-night',
    title: 'Lumora Overworld Theme - Night',
    file: '/music/Lumora Overworld Theme - Night.mp3',
  },
  'gaias-paradise-day': {
    id: 'gaias-paradise-day',
    title: "Gaia's Paradise",
    file: "/music/Gaia's Paradise (Neutral Faction Theme Daytime).mp3",
  },
  'gaias-paradise-night': {
    id: 'gaias-paradise-night',
    title: "Gaia's Paradise (Night)",
    file: "/music/Gaia's Paradise (Night).mp3",
  },
};

/**
 * Get a track by its ID
 */
export function getTrack(trackId: string): MusicTrack | undefined {
  return musicTracks[trackId];
}

/**
 * Get all tracks as an array (in order)
 */
export function getAllTracks(): MusicTrack[] {
  return Object.values(musicTracks);
}

/**
 * Track IDs exported for easy reference in MDX files
 */
export const TRACK_IDS = {
  LUMORA_FIRST_DAWN: 'lumora-first-dawn',
  PEACEFUL_VOYAGE_DAYLIGHT: 'peaceful-voyage-daylight',
  PEACEFUL_VOYAGE_BATTLE: 'peaceful-voyage-battle',
  PORTSMUTH_DAY: 'portsmuth-day',
  PORTSMUTH_NIGHT: 'portsmuth-night',
  FATEFUL_ENCOUNTER: 'fateful-encounter',
  MOONLIGHT_PURSUIT: 'moonlight-pursuit',
  CHAINS_BENEATH_CROWN: 'chains-beneath-crown',
  BLADES_OF_RESOLVE: 'blades-of-resolve',
  ECHOES_ASCETIC_DAY: 'echoes-ascetic-day',
  ECHOES_ASCETIC_NIGHT: 'echoes-ascetic-night',
  THRONE_IRON_DAY: 'throne-iron-day',
  THRONE_IRON_NIGHT: 'throne-iron-night',
  CROWN_SCEPTER_DAY: 'crown-scepter-day',
  CROWN_SCEPTER_NIGHT: 'crown-scepter-night',
  OVERWORLD_DAY: 'overworld-day',
  OVERWORLD_NIGHT: 'overworld-night',
  GAIAS_PARADISE_DAY: 'gaias-paradise-day',
  GAIAS_PARADISE_NIGHT: 'gaias-paradise-night',
} as const;
